// src/api/index.js
import axios from 'axios';

// Create axios instance
const apiClient = axios.create({
  baseURL: process.env.REACT_APP_API_URL || '/api',
  headers: {
    'Content-Type': 'application/json',
  },
});

// Force HTTPS protocol for all absolute URLs
const forceHttps = (url) => {
  if (!url) return url;
  
  // If it's a relative URL, just return it
  if (!url.includes('http')) return url;
  
  try {
    const urlObj = new URL(url);
    urlObj.protocol = 'https:';
    return urlObj.toString();
  } catch (e) {
    console.error('Error parsing URL:', e);
    return url;
  }
};

// Request interceptor for adding auth token
apiClient.interceptors.request.use(
  (config) => {
    // console.log(`API Request: ${config.method.toUpperCase()} ${config.url}`);
    
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    
    // Force HTTPS for all requests to categorix.ai domain
    if (config.url) {
      if (config.url.includes('categorix.ai')) {
        config.url = forceHttps(config.url);
      }
      
      // Also update baseURL if it uses categorix.ai
      if (config.baseURL && config.baseURL.includes('categorix.ai')) {
        config.baseURL = forceHttps(config.baseURL);
      }
    }
    
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for handling token expiration
apiClient.interceptors.response.use(
  (response) => {
    // console.log(`API Response: ${response.status} from ${response.config.url}`, response.data);
    
    return response;
  },
  async (error) => {
    // console.error(`API Error: ${error.response?.status || 'Network Error'} from ${error.config?.url || 'unknown'}`, error.response?.data);
    
    const originalRequest = error.config;
    
    // If error is 401 and we haven't already tried refreshing the token
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;
      
      try {
        // Try to refresh token
        const token = localStorage.getItem('token');
        if (!token) {
          throw new Error('No token found');
        }
        
        const response = await apiClient.post('/auth/refresh-token');
        const { access_token } = response.data;
        
        // Save new token
        localStorage.setItem('token', access_token);
        
        // Update header
        originalRequest.headers.Authorization = `Bearer ${access_token}`;
        
        // Retry the original request
        return apiClient(originalRequest);
      } catch (refreshError) {
        // If refresh fails, clear tokens and redirect to login
        localStorage.removeItem('token');
        window.location.href = '/login';
        return Promise.reject(refreshError);
      }
    }
    
    return Promise.reject(error);
  }
);

// API modules
const api = {
  // Set auth token
  setAuthToken: (token) => {
    apiClient.defaults.headers.common.Authorization = `Bearer ${token}`;
  },
  
  // Clear auth token
  clearAuthToken: () => {
    delete apiClient.defaults.headers.common.Authorization;
  },
  
  // Force all URLs to be HTTPS
  forceHttps,
  
  auth: {
    login: (email, password) => {
      // Use the simple login endpoint which handles both username/email formats
      return apiClient.post('/auth/login-simple', {
        username: email,
        password: password
      });
    },
    
    register: (userData) => 
      apiClient.post('/auth/register', userData),
    
    me: () => 
      apiClient.get('/auth/me'),
    
    refreshToken: () => 
      apiClient.post('/auth/refresh-token'),
      
    // Password reset
    forgotPassword: (email) => {
      // Use the direct implementation that bypasses FastAPI validation
      return apiClient.post('/auth/forgot-password-direct', 
        { email: email },
        {
          headers: {
            'Content-Type': 'application/json'
          }
        }
      );
    },
    
    // Form-based password reset (fallback)
    forgotPasswordForm: (email) => {
      // Create form data object
      const formData = new FormData();
      formData.append('email', email);
      
      return apiClient.post('/auth/forgot-password-form', 
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        }
      );
    },
      
    resetPassword: (token, newPassword, confirmPassword) =>
      apiClient.post('/auth/reset-password', {
        token,
        new_password: newPassword,
        confirm_password: confirmPassword
      }),
      
    // Email verification  
    verifyEmail: (token) =>
      apiClient.post('/auth/verify-email', { token }),
      
    // Resend verification email
    resendVerification: (email) =>
      apiClient.post('/auth/resend-verification', { email }),
  },

  // Users endpoints
  users: {
    updateProfile: (userData) => 
      apiClient.put('/users/me', userData),
    
    changePassword: (currentPassword, newPassword) => 
      apiClient.post('/users/change-password', { 
        current_password: currentPassword, 
        new_password: newPassword 
      }),
    
    getApiKeys: () => 
      apiClient.get('/users/api-keys'),
    
    createApiKey: (name) => 
      apiClient.post('/users/api-keys', { name }),
    
    deleteApiKey: (keyId) => 
      apiClient.delete(`/users/api-keys/${keyId}`),
  },
  
  // Taxonomy endpoints
  taxonomies: {
    list: (params) => 
      apiClient.get('/taxonomies', { params }),
    
    getSystemTaxonomies: (params) =>
      apiClient.get('/taxonomies/system', { params }),
    
    get: (id) => 
      apiClient.get(`/taxonomies/${id}`),
    
    create: (taxonomyData) => 
      apiClient.post('/taxonomies', taxonomyData),
    
    update: (id, taxonomyData) => 
      apiClient.put(`/taxonomies/${id}`, taxonomyData),
    
    delete: (id) => 
      apiClient.delete(`/taxonomies/${id}`),
    
    import: (formData) => 
      apiClient.post('/taxonomies/import', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }),
    
    clone: (id, name) => 
      apiClient.post(`/taxonomies/${id}/clone`, { name }),
  },
  
  // Mapping endpoints
  mapping: {
    listSessions: (params) => 
      apiClient.get('/mapping/sessions', { 
        params: { 
          ...params,
          include_deleted: params.include_deleted || false 
        } 
      }),
    
    getSession: (id) => 
      apiClient.get(`/mapping/sessions/${id}`),
    
    updateSession: (id, sessionData) => 
      apiClient.put(`/mapping/sessions/${id}`, sessionData),
    
    uploadFile: (file) => {
      const formData = new FormData();
      formData.append('file', file);
      
      return apiClient.post('/mapping/upload-file', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
    },
    
    getFileData: (sessionId) =>
      apiClient.get(`/mapping/file-data/${sessionId}`),
    
    startProcessing: (sessionId) => 
      apiClient.post('/mapping/process', { session_id: sessionId }),
    
    resumeProcessing: (sessionId) => 
      apiClient.post('/mapping/resume', { session_id: sessionId }),
    
    checkProgress: (sessionId) => 
      apiClient.get(`/mapping/progress/${sessionId}`),
    
    listMappings: (sessionId, params) => 
      apiClient.get(`/mapping/sessions/${sessionId}/mappings`, { params }),
    
    updateMapping: (mappingId, mappingData) => 
      apiClient.put(`/mapping/mappings/${mappingId}`, mappingData),
    
    getStatistics: (sessionId) => 
      apiClient.get(`/mapping/sessions/${sessionId}/stats`),
    
    downloadResults: (sessionId, options = {}) => 
      apiClient.get(`/mapping/sessions/${sessionId}/download`, {
        responseType: 'blob',
        ...options
      }),

    getSessionStats: () =>
      apiClient.get('/mapping/sessions-stats'),

    deleteSession: (sessionId) =>
      apiClient.delete(`/mapping/sessions/${sessionId}`),
  },
  
  // Payment endpoints
  payments: {
    getPlans: () => 
      apiClient.get('/payments/plans'),
    
    getSubscription: () => 
      apiClient.get('/payments/subscription'),
    
    createSubscription: (planId, paymentMethodId) => 
      apiClient.post('/payments/create-subscription', {
        plan_id: planId,
        payment_method_id: paymentMethodId,
      }),
    
    cancelSubscription: () => 
      apiClient.post('/payments/cancel-subscription'),
      
    activateFreePlan: () =>
      apiClient.post('/payments/activate-free-plan'),
      
    createCheckoutSession: (planId, successUrl, cancelUrl, billingPeriod = 'monthly') => 
      apiClient.post('/payments/create-checkout-session', {
        plan_id: planId,
        success_url: successUrl, 
        cancel_url: cancelUrl,
        billing_period: billingPeriod
      }),
      
    createPortalSession: (returnUrl) => 
      apiClient.post('/payments/create-portal-session', {
        return_url: returnUrl
      }),
      
    changePlan: (planId, immediatePayment = false) =>
      apiClient.post('/payments/change-plan', {
        plan_id: planId,
        immediate_payment: immediatePayment
      }),
      
    reactivateSubscription: () =>
      apiClient.post('/payments/reactivate-subscription'),
  },

  // Contact form endpoint
  contact: {
    submitForm: (formData) =>
      apiClient.post('/contact', {
        name: formData.name,
        email: formData.email,
        company: formData.company,
        phone: formData.phone,
        monthly_files: formData.monthlyFiles,
        rows_per_file: formData.rowsPerFile,
        message: formData.message,
        plan: formData.plan,
        inquiry: formData.inquiry
      }),
  },
};

export default api;